import ConfirmPublishModal from "@/modules/agent/components/VendorEmail/ConfirmPublishModal";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { addNotification } from "@/shared/states/notification";
import { UploadedFile } from "@/shared/types/file-upload";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ThirdPartyVendorLayout } from ".";
import { useMarkQuestionnaireCompleted, useSubmitVendorIntakeForm } from "../requests";
import { ChatMessage } from "../types";
import SimpleTPEChat from "./components/SimpleTPEChat";

interface SuccessScreenProps {
  token: string;
  chatMessages?: ChatMessage[];
  isLoadingMessages?: boolean;
  sessionId?: string;
  onMarkCompleted?: () => void;
  onUserMessageSent?: (message: ChatMessage) => void;
}

const SuccessScreen: React.FC<SuccessScreenProps> = ({ 
  token, 
  chatMessages = [], 
  isLoadingMessages = false, 
  sessionId,
  onMarkCompleted,
  onUserMessageSent
}) => {
  const [uploadedChatFiles, setUploadedChatFiles] = useState<UploadedFile[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  
  const { mutate: submitVendorDetails } = useSubmitVendorIntakeForm();
  const { mutate: markCompleted, isPending: isMarkingCompleted } = useMarkQuestionnaireCompleted();

  // Chat file upload handlers
  const handleChatFileUpload = useCallback(async (files: File[]) => {
    const newUploadedFiles: UploadedFile[] = files.map(file => ({
      id: uuidv4(),
      file: file,
      preview: URL.createObjectURL(file),
      status: 'gettingurl' as const,
      progressBar: false,
      signedUrl: undefined,
    }));
    
    // Add files to state immediately as pending
    setUploadedChatFiles(prev => [...prev, ...newUploadedFiles]);
    
    // Process each file for upload
    for (const uploadedFile of newUploadedFiles) {
      try {
        // Update status to inprogress
        setUploadedChatFiles(prev => 
          prev.map(f => f.id === uploadedFile.id ? { ...f, status: 'inprogress' as const } : f)
        );
        
        // Get signed URL
        const signedUrls = await getSignedUrl({
          file_names: [uploadedFile.file.name],
          sec_ctx: 'tpra_chat'
        });
        
        if (signedUrls && signedUrls.length > 0) {
          const signedUrl = signedUrls[0];
          
          // Upload file
          await uploadFileReq(signedUrl, uploadedFile.file);
          
          // Update file with signed URL and success status
          setUploadedChatFiles(prev => 
            prev.map(f => f.id === uploadedFile.id ? 
              { ...f, status: 'success' as const, signedUrl } : f
            )
          );
        } else {
          throw new Error('Failed to get signed URL');
        }
      } catch (error) {

        
        // Update status to failed
        setUploadedChatFiles(prev => 
          prev.map(f => f.id === uploadedFile.id ? { ...f, status: 'failed' as const } : f)
        );
        
        addNotification({
          type: 'error',
          message: `Failed to upload ${uploadedFile.file.name}`,
        });
      }
    }
  }, []);

  const handleRemoveChatFile = useCallback((fileId: string) => {
    setUploadedChatFiles(prev => {
      const fileToRemove = prev.find(file => file.id === fileId);
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(file => file.id !== fileId);
    });
  }, []);

  const handleOpenConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  const handleConfirmSubmission = useCallback(() => {
    if (!token) {
      addNotification({
        message: "Token is missing. Please refresh the page.",
        type: "error",
      });
      return;
    }

    markCompleted(token, {
      onSuccess: (response) => {
        if (response.success) {
          // Navigate to AgentWaiting instead of showing notification
          onMarkCompleted?.();
        } else {
          addNotification({
            message: response.message || "Failed to mark assessment as complete.",
            type: "error",
          });
        }
      },
      onSettled: () => {
        setIsConfirmModalOpen(false);
      },
      onError: (error: any) => {

        addNotification({
          message: "Failed to mark assessment as complete. Please try again.",
          type: "error",
        });
      }
    });
  }, [token, markCompleted, onMarkCompleted]);

  return <ThirdPartyVendorLayout>
    <div className="h-full w-full flex flex-col">
      {/* Breadcrumb outside white background - matching questionnaire style */}
      <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px] px-0">
        <div className="flex gap-4 items-center">
          <ZBreadCrumbs
            items={[
              { title: "Vendor Due Diligence", href: "" }
            ]}
          />
        </div>
        <div className="flex gap-2">
          <ZButton
            color="primary"
            className="text-xs font-medium h-7 px-3"
            onClick={handleOpenConfirmModal}
          >
            Submit
          </ZButton>
        </div>
      </div>

      {/* Chat Interface - Full width like questionnaire */}
      <div className="flex-1 min-h-0">
        <SimpleTPEChat
          onFileUpload={handleChatFileUpload}
          uploadedFiles={uploadedChatFiles}
          onRemoveFile={handleRemoveChatFile}
          className="h-full"
          token={token}
          chatMessages={chatMessages}
          isLoadingMessages={isLoadingMessages}
          sessionId={sessionId}
          onMessageSent={onUserMessageSent}
        />
      </div>
    </div>

    <ConfirmPublishModal
      isOpen={isConfirmModalOpen}
      onClose={handleCloseConfirmModal}
      onConfirm={handleConfirmSubmission}
      title="Confirm Completion?"
      isLoading={isMarkingCompleted}
    />
  </ThirdPartyVendorLayout>
}

export default SuccessScreen;